import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SignatureCanvas from "@/components/signatures/SignatureCanvas";
import { Upload, Trash2, PenLine, ShieldCheck } from "lucide-react";

// The single own-signature editor. Mounted in the top-right account menu (My
// Signature) for every user. Acts ONLY on the caller's own signature.
export default function SignatureEditor() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["my-signature"],
    queryFn: async () => (await api.get("/signatures/me")).data,
  });
  const sig = data?.signature;

  useEffect(() => {
    if (data) {
      setName(sig?.printed_name || data.default_printed_name || "");
      setTitle(sig?.title || "");
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["my-signature"] });
    qc.invalidateQueries({ queryKey: ["signatures-all"] });
  };
  const fail = (e, fb) => toast.error(formatApiError(e) || fb);

  const uploadMut = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/signatures/me/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => { toast.success("Signature saved"); refresh(); },
    onError: (e) => fail(e, "Could not upload the image"),
  });
  const drawMut = useMutation({
    mutationFn: async (dataUri) => (await api.post("/signatures/me/draw", { data_uri: dataUri })).data,
    onSuccess: () => { toast.success("Signature saved"); refresh(); },
    onError: (e) => fail(e, "Could not save the drawing"),
  });
  const profileMut = useMutation({
    mutationFn: async () => (await api.patch("/signatures/me", { printed_name: name, title })).data,
    onSuccess: () => { toast.success("Updated"); refresh(); },
    onError: (e) => fail(e, "Could not update"),
  });
  const consentMut = useMutation({
    mutationFn: async (enabled) => (await api.post("/signatures/me/consent", { enabled })).data,
    onSuccess: (d) => { toast.success(d.signature?.auto_apply_consent ? "Auto-apply turned on" : "Auto-apply turned off"); refresh(); },
    onError: (e) => fail(e, "Could not change the setting"),
  });
  const removeMut = useMutation({
    mutationFn: async () => (await api.delete("/signatures/me")).data,
    onSuccess: () => { toast.success("Signature removed"); refresh(); },
    onError: (e) => fail(e, "Could not remove"),
  });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) uploadMut.mutate(f);
    e.target.value = "";
  };

  if (isLoading) return <div className="py-8 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="signature-editor">
      {sig?.has_image && (
        <div>
          <Label className="text-xs text-muted-foreground">Current signature — previewed on light and dark</Label>
          <div className="grid grid-cols-2 gap-3 mt-1.5">
            <div className="rounded-lg bg-white border border-border p-3 h-28 flex items-center justify-center">
              <img src={sig.image} alt="signature on white" className="max-h-full max-w-full object-contain" data-testid="signature-preview-light" />
            </div>
            <div className="rounded-lg bg-zinc-900 border border-border p-3 h-28 flex items-center justify-center">
              <img src={sig.image} alt="signature on dark" className="max-h-full max-w-full object-contain invert" data-testid="signature-preview-dark" />
            </div>
          </div>
          {!sig.is_active && <p className="text-xs text-amber-500 mt-1.5">This signature has been retired by an admin and won't be applied.</p>}
        </div>
      )}

      <Tabs defaultValue="draw">
        <TabsList>
          <TabsTrigger value="draw" data-testid="sig-tab-draw"><PenLine className="h-3.5 w-3.5 mr-1.5" />Draw</TabsTrigger>
          <TabsTrigger value="upload" data-testid="sig-tab-upload"><Upload className="h-3.5 w-3.5 mr-1.5" />Upload</TabsTrigger>
        </TabsList>
        <TabsContent value="draw" className="mt-3">
          <SignatureCanvas onSave={(d) => drawMut.mutate(d)} saving={drawMut.isPending} />
        </TabsContent>
        <TabsContent value="upload" className="mt-3">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml" className="hidden" onChange={onFile} data-testid="signature-file-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploadMut.isPending} data-testid="signature-upload-button">
            <Upload className="h-4 w-4 mr-1.5" />{uploadMut.isPending ? "Uploading…" : (sig?.has_image ? "Replace with a file" : "Choose an image")}
          </Button>
          <p className="text-xs text-muted-foreground mt-2">PNG (transparent background prints best), JPG or SVG. Max 2 MB.</p>
        </TabsContent>
      </Tabs>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Printed name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="signature-printed-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Title <span className="text-muted-foreground">(optional)</span></Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Sales Manager" data-testid="signature-title" />
        </div>
      </div>
      <Button size="sm" variant="secondary" onClick={() => profileMut.mutate()} disabled={profileMut.isPending} data-testid="signature-save-profile">Save name &amp; title</Button>

      <div className="flex items-start justify-between gap-4 rounded-xl border border-border p-4 bg-secondary/30">
        <div className="flex-1">
          <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /><span className="text-sm font-medium">Automatically apply my signature to documents I generate.</span></div>
          <p className="text-xs text-muted-foreground mt-1">Off by default. No document uses your signature until you turn this on. {sig?.consent_given_at ? `On since ${new Date(sig.consent_given_at).toLocaleDateString()}.` : ""}</p>
        </div>
        <Switch
          checked={!!sig?.auto_apply_consent}
          disabled={!sig?.has_image || consentMut.isPending}
          onCheckedChange={(v) => consentMut.mutate(v)}
          data-testid="signature-consent-toggle"
        />
      </div>

      {sig?.has_image && (
        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => { if (window.confirm("Remove your saved signature?")) removeMut.mutate(); }} data-testid="signature-remove">
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />Remove my signature
        </Button>
      )}
    </div>
  );
}
